import { useId } from 'react';
import { Lock } from 'lucide-react';
import { BADGE_ICONS } from '@/lib/badgeSystem';
import { cn } from '@/lib/utils';

export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';
export type BadgeSize = 'sm' | 'md' | 'lg' | 'xl';

interface EnhancedBadgeIconProps {
  badgeId?: string;
  rarity?: BadgeRarity;
  size?: BadgeSize;
  earned?: boolean;
  progress?: number;
  className?: string;
}

const RARITY_STYLES = {
  common: {
    gradient: ['#64748b', '#94a3b8'],
    border: '#9ca3af',
    glow: 'rgba(100, 116, 139, 0.4)',
    glowIntensity: 8,
  },
  rare: {
    gradient: ['#2563eb', '#0891b2'],
    border: '#355A99',
    glow: 'rgba(53, 90, 153, 0.5)',
    glowIntensity: 12,
  },
  epic: {
    gradient: ['#7c3aed', '#c026d3'],
    border: '#e879f9',
    glow: 'rgba(139, 92, 246, 0.5)',
    glowIntensity: 14,
  },
  legendary: {
    gradient: ['#d97706', '#f59e0b'],
    border: '#fbbf24',
    glow: 'rgba(245, 158, 11, 0.6)',
    glowIntensity: 18,
  },
};

const SIZE_CONFIG = {
  sm: { container: 40, icon: 18, border: 2, lockSize: 12 },
  md: { container: 56, icon: 26, border: 2.5, lockSize: 14 },
  lg: { container: 72, icon: 34, border: 3, lockSize: 18 },
  xl: { container: 96, icon: 44, border: 3.5, lockSize: 22 },
};

export function EnhancedBadgeIcon({
  badgeId,
  rarity = 'common',
  size = 'md',
  earned = true,
  progress,
  className,
}: EnhancedBadgeIconProps) {
  const uniqueId = useId();
  const styles = RARITY_STYLES[rarity];
  const sizeConfig = SIZE_CONFIG[size];
  
  // Get the Lucide icon for this badge
  const IconComponent = badgeId ? BADGE_ICONS[badgeId] : null;
  
  const isInProgress = !earned && progress !== undefined && progress > 0;
  const isLocked = !earned && !isInProgress;
  
  // Calculate progress ring
  const radius = (sizeConfig.container / 2) - 4;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = circumference - ((progress || 0) / 100) * circumference;
  
  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center',
        isLocked && 'grayscale opacity-50',
        earned && rarity === 'legendary' && 'animate-badge-pulse-glow',
        className
      )}
      style={{
        width: sizeConfig.container,
        height: sizeConfig.container,
      }}
    >
      {/* SVG Background with gradient and effects */}
      <svg
        width={sizeConfig.container}
        height={sizeConfig.container}
        viewBox={`0 0 ${sizeConfig.container} ${sizeConfig.container}`}
        className="absolute inset-0"
      >
        <defs>
          {/* Radial gradient for background */}
          <radialGradient id={`bg-gradient-${uniqueId}`} cx="30%" cy="30%" r="70%">
            <stop offset="0%" stopColor={styles.gradient[1]} />
            <stop offset="100%" stopColor={styles.gradient[0]} />
          </radialGradient>
          
          {/* Drop shadow filter */}
          <filter id={`shadow-${uniqueId}`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)" />
          </filter>
          
          {/* Glow filter for earned badges */}
          <filter id={`glow-${uniqueId}`} x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation={styles.glowIntensity / 2} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          
          {/* Shine gradient for animation */}
          <linearGradient id={`shine-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.3" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        
        {/* Outer glow circle (earned only) */}
        {earned && (
          <circle
            cx={sizeConfig.container / 2}
            cy={sizeConfig.container / 2}
            r={sizeConfig.container / 2 - 2}
            fill={styles.glow}
            filter={`url(#glow-${uniqueId})`}
            opacity="0.6"
          />
        )}
        
        {/* Main background circle */}
        <circle
          cx={sizeConfig.container / 2}
          cy={sizeConfig.container / 2}
          r={sizeConfig.container / 2 - 4}
          fill={`url(#bg-gradient-${uniqueId})`}
          filter={`url(#shadow-${uniqueId})`}
        />
        
        {/* Metallic border ring */}
        <circle
          cx={sizeConfig.container / 2}
          cy={sizeConfig.container / 2}
          r={sizeConfig.container / 2 - 4}
          fill="none"
          stroke={styles.border}
          strokeWidth={sizeConfig.border}
          opacity="0.8"
        />
        
        {/* Inner highlight for 3D effect */}
        <circle
          cx={sizeConfig.container / 2 - sizeConfig.container * 0.15}
          cy={sizeConfig.container / 2 - sizeConfig.container * 0.15}
          r={sizeConfig.container * 0.2}
          fill="white"
          opacity="0.15"
        />
        
        {/* Progress ring (in-progress only) */}
        {isInProgress && (
          <circle
            cx={sizeConfig.container / 2}
            cy={sizeConfig.container / 2}
            r={radius}
            fill="none"
            stroke="white"
            strokeWidth={sizeConfig.border + 1}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            transform={`rotate(-90 ${sizeConfig.container / 2} ${sizeConfig.container / 2})`}
            opacity="0.7"
          />
        )}
        
        {/* Shine overlay for earned badges */}
        {earned && (
          <circle
            cx={sizeConfig.container / 2}
            cy={sizeConfig.container / 2}
            r={sizeConfig.container / 2 - 5}
            fill={`url(#shine-${uniqueId})`}
            className="animate-badge-shine"
            style={{ transformOrigin: 'center' }}
          />
        )}
      </svg>
      
      {/* Lucide Icon centered */}
      {IconComponent && (
        <IconComponent
          size={sizeConfig.icon}
          strokeWidth={2.5}
          className={cn(
            'relative z-10',
            earned ? 'text-white drop-shadow-md' : 'text-white/70'
          )}
        />
      )}
      
      {/* Lock overlay for locked badges */}
      {isLocked && (
        <div
          className="absolute bottom-0 right-0 z-20 rounded-full bg-slate-700 p-0.5 shadow-md"
          style={{
            width: sizeConfig.lockSize + 4,
            height: sizeConfig.lockSize + 4,
          }}
        >
          <Lock size={sizeConfig.lockSize} className="text-slate-300" />
        </div>
      )}
      
      {/* Sparkle particles for legendary earned badges */}
      {earned && rarity === 'legendary' && (
        <>
          <div 
            className="absolute w-1 h-1 bg-amber-300 rounded-full animate-badge-sparkle"
            style={{ top: '10%', left: '20%', animationDelay: '0s' }}
          />
          <div 
            className="absolute w-1.5 h-1.5 bg-yellow-200 rounded-full animate-badge-sparkle"
            style={{ top: '15%', right: '15%', animationDelay: '0.5s' }}
          />
          <div 
            className="absolute w-1 h-1 bg-amber-400 rounded-full animate-badge-sparkle"
            style={{ bottom: '20%', left: '15%', animationDelay: '1s' }}
          />
          <div 
            className="absolute w-1 h-1 bg-yellow-300 rounded-full animate-badge-sparkle"
            style={{ bottom: '15%', right: '20%', animationDelay: '1.5s' }}
          />
        </>
      )}
    </div>
  );
}
